import { getCurrencySymbol } from "@hl/shared-features/lib/utils/currency";
import { formatNumber } from "@hl/shared-features/lib/utils/format";
import { Group, useMantineTheme } from "@mantine/core";
import { useCollections, useTokens } from "@reservoir0x/reservoir-kit-ui";
import dayjs from "dayjs";

import MarketplaceStat from "~features/MintPage/apollo/MarketplaceStat";
import MarketplaceFooterSkeleton from "~features/MintPage/apollo/skeleton/MarketplaceFooterSkeleton";
import useMintState from "~hooks/useMintState";

import { _CollectionType } from "../../../apollo/graphql.generated";

const MarketplaceStatsHeader = ({
  invertColors,
}: {
  invertColors?: boolean;
}) => {
  const {
    chainId,
    reservoirCollectionId,
    isImported,
    totalMintEarned,
    totalSoldCountLive,
    mintVector,
    auction,
    isERC1155,
    collection,
  } = useMintState();
  const theme = useMantineTheme();
  const hasMarketplaceStats =
    collection?.collectionType !== _CollectionType.LimitedEdition;
  const hasCollectionStats =
    hasMarketplaceStats &&
    collection?.collectionType !== _CollectionType.OneOfOne &&
    (!isERC1155 || isImported);
  const hasTokenStats = hasMarketplaceStats && !hasCollectionStats;
  const collectionsQuery = hasCollectionStats
    ? {
        id: reservoirCollectionId,
      }
    : undefined;
  const { data, isLoading: isLoadingCollection } = useCollections(
    collectionsQuery,
    undefined,
    chainId
  );

  const tokenIdentifier =
    (collection?.onchainId?.split(":").length || 0) > 2
      ? collection?.onchainId?.split(":").slice(1).join(":")
      : undefined;

  const tokensQuery =
    hasTokenStats && tokenIdentifier
      ? {
          tokens: [tokenIdentifier],
          includeTopBid: true,
          includeQuantity: true,
        }
      : undefined;
  const { data: tokensData, isLoading: isLoadingToken } = useTokens(
    tokensQuery,
    undefined,
    chainId
  );
  const isLoading = isLoadingCollection || isLoadingToken;

  const marketplaceCollection = data[0];
  const marketplaceToken = tokensData?.[0];

  const currency = getCurrencySymbol(chainId || "");

  const totalVolumeFormatted = formatNumber(
    marketplaceCollection?.volume?.allTime || 0
  );
  const floorAskFormatted = formatNumber(
    hasCollectionStats
      ? marketplaceCollection?.floorAsk?.price?.amount?.native || 0
      : marketplaceToken?.market?.floorAsk?.price?.amount?.native || 0
  );
  const topBidFormatted = formatNumber(
    hasCollectionStats
      ? marketplaceCollection?.topBid?.price?.amount?.native || 0
      : marketplaceToken?.market?.topBid?.price?.amount?.native || 0
  );
  const percentListed =
    +(marketplaceCollection?.tokenCount ?? 0) > 0
      ? `${
          Math.floor(
            (+(marketplaceCollection.onSaleCount ?? 0) /
              +(marketplaceCollection.tokenCount ?? 0)) *
              1000
          ) / 10
        }%`
      : 0;
  const ownerCount = marketplaceCollection?.ownerCount || 0;
  const ownerCountFormatted = ownerCount
    ? Intl.NumberFormat().format(ownerCount)
    : 0;

  const createdAt = dayjs(
    mintVector?.start || auction?.createdAt || marketplaceCollection?.createdAt
  ).format("MMM YYYY");

  const collectionSize = isImported
    ? parseInt(marketplaceCollection?.tokenCount ?? "0")
    : totalSoldCountLive;
  const collectionSizeFormatted = collectionSize
    ? Intl.NumberFormat().format(collectionSize)
    : 0;

  return !isLoading ? (
    <Group
      spacing={32}
      noWrap
      sx={{
        textWrap: "nowrap",
        color: invertColors ? theme.colors.invertedPrimaryText[0] : undefined,
      }}
    >
      {createdAt && <MarketplaceStat value={createdAt} label="Created" />}
      {!isImported && (
        <MarketplaceStat
          value={totalMintEarned}
          label="Primary sales"
          suffix={currency}
        />
      )}
      <MarketplaceStat
        value={collectionSizeFormatted}
        label="Collection size"
      />
      {(marketplaceCollection || marketplaceToken) && (
        <>
          {marketplaceCollection && (
            <MarketplaceStat
              value={totalVolumeFormatted}
              label="Secondary sales"
              suffix={currency}
            />
          )}
          <MarketplaceStat
            value={floorAskFormatted}
            suffix={currency}
            label="Floor price"
          />
          <MarketplaceStat
            value={topBidFormatted}
            suffix={currency}
            label="Best offer"
          />
          {marketplaceCollection && (
            <MarketplaceStat value={percentListed} label="Listed" />
          )}
          {marketplaceCollection && (
            <MarketplaceStat value={ownerCountFormatted} label="Owners" />
          )}
        </>
      )}
    </Group>
  ) : (
    <MarketplaceFooterSkeleton />
  );
};

export default MarketplaceStatsHeader;
